import _ from "lodash";
import React, { memo, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { useResize } from "Hooks";
import { isIdEqual } from "Utils";
import {
  CHAT_TYPE,
  MESSAGE_STATUS,
  MESSAGE_TYPE,
  getFormattedNameForUserType,
  getMessageDOMId,
  getParentContainer,
  getIsMessagingWidget,
  getIsViewMode,
  NORMAL_MESSAGE_ASSIGNMENT_ACTIVITY_TYPES,
  NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES,
} from "Utils/MessageHelpers";
import MessageOptionsProvider, {
  useMessageOptions,
} from "./providers/MessageProvider";

import UnreadMessageLine from "./components/UnreadMessageLine";
import MessageBubble from "./components/MessageBubble";
import SummaryMessage from "./components/SummaryMessage";
import FailedMessageFooter from "./components/FailedMessageFooter";
import MessageOptions from "./components/MessageOptions";
import MessageActivity from "./components/MessageActivity";
import ReadReceipt from "./components/ReadReceipt";
import UserAvatar, {
  UserName,
  AVATAR_RENDER_MODES,
} from "./components/UserAvatar";
import MessageReactions from "./components/MessageReactions";
import DateMessage from "./components/DateMessage";
import DeletedMessage from "./components/DeletedMessage";
import AssignmentActivity from "./components/AssignmentActivity";

import classes from "./Message.scss";

const Message = props => {
  const {
    message,
    chatType,
    openPreviewModal,
    isNextConsecutiveMessage,
    isEmojiPickerOpenedForMessage,
    setMainEmojiPickerState,
    onEditMessageClick,
    onRetryMessageClick,
    onReplyMessageClick,
    onParentMessageClick,
    isMessageHighlighted,
    defaultNameSortPreference,
    getMessageElementIdCb,
    defaultStudentNameOrderPreference,
    isMessageOptionsVisible = true,
    status,
    isCurrentMessageSeperated,
    isNextMessageSeperated,
    searchText,
    isGeneratingSummary,
    cancelSummaryGeneration,
    getDebugPropsForSummaryMessage,
    removeDebugPropsForSummaryMessage,
    canModifyMessage = true,
    recordMessageActions,
    toggleReaction,
    deleteMessage,
    getPopupContainer,
    showReadReceipt,
    showReplyOption,
    showReactOption,
    studentData,
    plannerElementSets,
    allowDeleteForAllMessages,
    messageContainerStyle,
    renderLinksAsAnchor = true,
  } = props;

  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  const {
    screenMode,
    activeUserId: userId,
    chatId,
    isPreviewMessagesModal,
    conversationId,
  } = useMessageOptions();

  const parentId = chatId || conversationId;

  useEffect(() => {
    const rootContainer = getParentContainer(parentId); // add root container for more accuracy and pass the chatId as dependency, as we are using here.
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        root: rootContainer,
      }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [parentId]);

  const [openedDropdown, setOpenedDropdown] = useState(null);
  const [bubbleContainerRef, dimensions] = useResize();

  const isMessagingWidget = getIsMessagingWidget({ screenMode });
  const isViewMode = getIsViewMode({ screenMode });

  const {
    id: messageId,
    type,
    createdBy,
    reactions,
    isDeleted,
    isRead,
    itemType,
  } = message;
  const totalReactions = _.size(reactions);

  const isSender = isIdEqual(_.get(createdBy, "id"), userId);
  const isChannel = chatType === CHAT_TYPE.CHANNEL;

  const isFlaggedMessage = _.get(message, "isFlagged", false);
  const isMessageOptionsOpen =
    !_.isNull(openedDropdown) || isEmojiPickerOpenedForMessage;

  const containerClassV2 = classNames(classes.containerV2, {
    [classes.fullScreenContainer]: !isMessagingWidget,
    [classes.widgetContainerV2]: isMessagingWidget,
    [classes.highlightedMessageContainer]:
      isMessageHighlighted && (!isViewMode || !isFlaggedMessage),
    [classes.highlightedFlaggedMessageContainer]:
      isMessageHighlighted && isViewMode && isFlaggedMessage,
    [classes.persistentBackground]: isMessageOptionsOpen,
    [classes.containerPaddingBottom]: isNextMessageSeperated,
    [classes.containerPaddingTop]: isCurrentMessageSeperated,
  });

  const optionContainerClassesV2 = classNames(classes.optionsContainerV2, {
    [classes.persistentOptionsContainer]: isMessageOptionsOpen,
  });

  const handleRetryMessageClick = () => onRetryMessageClick(message);

  const fullName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  /* Failed or Sent Options */
  const showMessageOptions =
    isIntersecting &&
    status !== MESSAGE_STATUS.SENDING &&
    !isViewMode &&
    isMessageOptionsVisible;

  const isSent = !status;

  const isReadReceipt =
    !isViewMode && !isPreviewMessagesModal && showReadReceipt;
  const getMessageElementId = _.isFunction(getMessageElementIdCb)
    ? getMessageElementIdCb
    : getMessageDOMId;

  switch (type) {
    case MESSAGE_TYPE.NORMAL:
      if (
        _.includes(NORMAL_MESSAGE_ASSIGNMENT_ACTIVITY_TYPES, itemType) ||
        _.includes(NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES, itemType)
      ) {
        return (
          <AssignmentActivity
            message={message}
            studentData={studentData}
            plannerElementSets={plannerElementSets}
          />
        );
      }

      return (
        <div
          data-message={messageId}
          id={getMessageElementId(message)}
          ref={ref}
          className={containerClassV2}
          tabIndex={0}
          style={messageContainerStyle}
        >
          <UserAvatar
            user={createdBy}
            fullName={fullName}
            avatarRenderMode={
              isCurrentMessageSeperated
                ? AVATAR_RENDER_MODES.AVATAR
                : AVATAR_RENDER_MODES.SPACE
            }
          />
          <div className={classes.contentContainerV2}>
            {isCurrentMessageSeperated ? (
              <div className={classes.nameReceiptContainer}>
                <UserName
                  user={createdBy}
                  fullName={fullName}
                  createdAt={message?.createdAt}
                />
                {status !== MESSAGE_STATUS.FAILED && isReadReceipt ? (
                  <div className={classes.readReceiptClass}>
                    <ReadReceipt
                      isSender={isSender}
                      isSent={isSent}
                      isRead={isRead}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {isDeleted ? (
              <DeletedMessage isSender={isSender} />
            ) : (
              <>
                <div
                  ref={bubbleContainerRef}
                  className={classes.bubbleAndOptionsContainerV2}
                >
                  <MessageBubble
                    status={status}
                    message={message}
                    isSender={isSender}
                    openPreviewModal={openPreviewModal}
                    isMessagingWidget={isMessagingWidget}
                    onParentMessageClick={onParentMessageClick}
                    isMessageHighlighted={isMessageHighlighted}
                    searchText={searchText}
                    renderLinksAsAnchor={renderLinksAsAnchor}
                  />

                  {showMessageOptions ? (
                    <div className={optionContainerClassesV2}>
                      <MessageOptions
                        status={status}
                        message={message}
                        isSender={isSender}
                        isChannel={isChannel}
                        openedDropdown={openedDropdown}
                        setOpenedDropdown={setOpenedDropdown}
                        setMainEmojiPickerState={setMainEmojiPickerState}
                        onReplyMessageClick={onReplyMessageClick}
                        onEditMessageClick={onEditMessageClick}
                        shouldSeparateMessage={isCurrentMessageSeperated}
                        canModifyMessage={canModifyMessage}
                        recordMessageActions={recordMessageActions}
                        deleteMessage={deleteMessage}
                        getPopupContainer={getPopupContainer}
                        showReplyOption={showReplyOption}
                        showReactOption={showReactOption}
                        showReadReceipt={showReadReceipt}
                        allowDeleteForAllMessages={allowDeleteForAllMessages}
                      />
                    </div>
                  ) : null}
                </div>

                {/* Failed or Sent Message Footer */}
                {status === MESSAGE_STATUS.FAILED ? (
                  <FailedMessageFooter
                    onRetryMessageClick={handleRetryMessageClick}
                  />
                ) : totalReactions ? (
                  <MessageReactions
                    message={message}
                    reactions={reactions}
                    width={dimensions.width}
                    setMainEmojiPickerState={setMainEmojiPickerState}
                    isNextConsecutiveMessage={isNextConsecutiveMessage}
                    disableReaction={!isMessageOptionsVisible}
                    toggleReaction={toggleReaction}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    case MESSAGE_TYPE.CONVERSATION_SUMMARY: {
      return (
        <div
          id={getMessageElementId(message)}
          data-message={messageId}
          className={containerClassV2}
        >
          <SummaryMessage
            message={message}
            isGeneratingSummary={isGeneratingSummary}
            cancelSummaryGeneration={cancelSummaryGeneration}
            getDebugPropsForSummaryMessage={getDebugPropsForSummaryMessage}
            removeDebugPropsForSummaryMessage={
              removeDebugPropsForSummaryMessage
            }
          />
        </div>
      );
    }
    case MESSAGE_TYPE.CHANNEL_ACTIVITY: {
      return (
        <div
          id={getMessageElementId(message)}
          data-message={messageId}
          className={classes.channelActivityContainerV2}
        >
          <div>
            <MessageActivity message={message} />
          </div>
        </div>
      );
    }
    case MESSAGE_TYPE.ASSIGNMENT_ACTIVITY:
    case MESSAGE_TYPE.STUDENT_ASSIGNMENT_ACTIVITY:
    case MESSAGE_TYPE.ASSESSMENT_ACTIVITY:
      return (
        <AssignmentActivity
          message={message}
          studentData={studentData}
          plannerElementSets={plannerElementSets}
        />
      );
    default:
      return null;
  }
};

const CommonPropTypes = {
  openPreviewModal: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  isSameSenderAsPreviousMessage: PropTypes.bool.isRequired,
  chatType: PropTypes.oneOf(_.values(CHAT_TYPE)).isRequired,
  isNextConsecutiveMessage: PropTypes.bool.isRequired,
  setMainEmojiPickerState: PropTypes.func,
  isEmojiPickerOpenedForMessage: PropTypes.bool,
  onEditMessageClick: PropTypes.func.isRequired,
  onRetryMessageClick: PropTypes.func,
  onReplyMessageClick: PropTypes.func,
  onParentMessageClick: PropTypes.func,
  isMessageHighlighted: PropTypes.bool,
  isMessageOptionsVisible: PropTypes.bool,
  getMessageElementIdCb: PropTypes.func,
  status: PropTypes.oneOf(_.values(MESSAGE_STATUS)),
  isCurrentMessageSeperated: PropTypes.bool.isRequired,
  isNextMessageSeperated: PropTypes.bool.isRequired,
  searchText: PropTypes.string,
  isGeneratingSummary: PropTypes.bool,
  cancelSummaryGeneration: PropTypes.func,
  getDebugPropsForSummaryMessage: PropTypes.func,
  removeDebugPropsForSummaryMessage: PropTypes.func,
  canModifyMessage: PropTypes.bool,
  recordMessageActions: PropTypes.func,
  toggleReaction: PropTypes.func,
  deleteMessage: PropTypes.func,
  getPopupContainer: PropTypes.func,
  showReadReceipt: PropTypes.bool,
  showReplyOption: PropTypes.bool,
  showReactOption: PropTypes.bool,
  studentData: PropTypes.object,
  plannerElementSets: PropTypes.object,
  renderLinksAsAnchor: PropTypes.bool,
};

const defaultEmptyFunction = () => {};

Message.defaultProps = {
  setMainEmojiPickerState: defaultEmptyFunction,
  isEmojiPickerOpenedForMessage: false,
  onRetryMessageClick: defaultEmptyFunction,
  onReplyMessageClick: defaultEmptyFunction,
  onParentMessageClick: defaultEmptyFunction,
  isMessageHighlighted: false,
  searchText: "",
  isGeneratingSummary: false,
  cancelSummaryGeneration: defaultEmptyFunction,
  getDebugPropsForSummaryMessage: defaultEmptyFunction,
  removeDebugPropsForSummaryMessage: defaultEmptyFunction,
  canModifyMessage: true,
  recordMessageActions: defaultEmptyFunction,
  toggleReaction: defaultEmptyFunction,
  deleteMessage: defaultEmptyFunction,
  getPopupContainer: () => document.body,
  showReadReceipt: false,
  showReplyOption: false,
  showReactOption: false,
  studentData: {},
  plannerElementSets: {},
  renderLinksAsAnchor: true,
};

Message.propTypes = CommonPropTypes;

const MessageContainer = props => {
  const {
    isSameDateAsPreviousMessage,
    isUnreadLineVisible,
    message,
    unreadMessageCount,
    chatId,
    conversationId,
    screenMode,
    activeUserId,
    isPreviewMessagesModal,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    currentUserType,
    showUnreadMessageButton,
    showSummariseNewMessagesWithAi,
    isAISummariseEnabled = false,
    setShowUnreadMessageButton,
    markConversationAsRead,
    generateSummaryForNewMessages,
    curriculumProgramType,
  } = props;
  const { createdAt, id } = message;

  return (
    <MessageOptionsProvider
      chatId={chatId}
      conversationId={conversationId}
      screenMode={screenMode}
      activeUserId={activeUserId}
      isPreviewMessagesModal={isPreviewMessagesModal}
      defaultNameSortPreference={defaultNameSortPreference}
      defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
      currentUserType={currentUserType}
      curriculumProgramType={curriculumProgramType}
    >
      {isUnreadLineVisible ? (
        <UnreadMessageLine
          messageId={id}
          unreadMessageCount={unreadMessageCount}
          showUnreadMessageButton={showUnreadMessageButton}
          showSummariseNewMessagesWithAi={showSummariseNewMessagesWithAi}
          isAISummariseEnabled={isAISummariseEnabled}
          setShowUnreadMessageButton={setShowUnreadMessageButton}
          markConversationAsRead={markConversationAsRead}
          generateSummaryForNewMessages={generateSummaryForNewMessages}
        />
      ) : null}
      {isSameDateAsPreviousMessage ? null : <DateMessage date={createdAt} />}
      <Message {...props} />
    </MessageOptionsProvider>
  );
};

MessageContainer.propTypes = {
  ...CommonPropTypes,
  chatId: PropTypes.string,
  conversationId: PropTypes.string,
  screenMode: PropTypes.string.isRequired,
  activeUserId: PropTypes.string.isRequired,
  isPreviewMessagesModal: PropTypes.bool,
  isSameDateAsPreviousMessage: PropTypes.bool.isRequired,
  defaultNameSortPreference: PropTypes.string.isRequired,
  defaultStudentNameOrderPreference: PropTypes.string.isRequired,
  currentUserType: PropTypes.string.isRequired,
  showUnreadMessageButton: PropTypes.bool,
  showSummariseNewMessagesWithAi: PropTypes.bool,
  isAISummariseEnabled: PropTypes.bool,
  setShowUnreadMessageButton: PropTypes.func,
  markConversationAsRead: PropTypes.func,
  generateSummaryForNewMessages: PropTypes.func,
  curriculumProgramType: PropTypes.string,
};

export default memo(MessageContainer);
